import { useQuery } from "@apollo/client/react";
import { HiSearchCircle } from "react-icons/hi";
import {
  LearningVelocityDocument,
  PerformanceTrendsDocument,
  ReviewTimePatternsDocument,
  StreakAnalysisDocument,
} from "../../../generated/graphql";
import type { FilterState } from "../../../pages/fsrs/AnalyticsDashboardPage";
import { LearningVelocityChart } from "../charts/LearningVelocityChart";
import { PerformanceTrendChart } from "../charts/PerformanceTrendChart";
import { ReviewTimeChart } from "../charts/ReviewTimeChart";
import { StreakDisplay } from "../charts/StreakDisplay";

interface PatternsTabProps {
  filters: FilterState;
}

export function PatternsTab({ filters }: PatternsTabProps) {
  const { selectedStudentId } = filters;

  const { data: timePatternData, loading: timePatternLoading } = useQuery(
    ReviewTimePatternsDocument,
    {
      variables: {
        studentId: selectedStudentId || "",
        dateRange: null,
      },
      skip: !selectedStudentId,
    }
  );

  const { data: performanceData, loading: performanceLoading } = useQuery(
    PerformanceTrendsDocument,
    {
      variables: {
        studentId: selectedStudentId || "",
        dateRange: null,
      },
      skip: !selectedStudentId,
    }
  );

  const { data: velocityData, loading: velocityLoading } = useQuery(LearningVelocityDocument, {
    variables: {
      studentId: selectedStudentId || "",
      dateRange: null,
    },
    skip: !selectedStudentId,
  });

  const { data: streakData, loading: streakLoading } = useQuery(StreakAnalysisDocument, {
    variables: {
      studentId: selectedStudentId || "",
    },
    skip: !selectedStudentId,
  });

  // Empty state if no student selected
  if (!selectedStudentId) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <HiSearchCircle className="text-6xl mb-4 mx-auto text-gray-600" />
          <h3 className="h5 mb-2">Select a Student</h3>
          <p className="text-gray-600">
            Choose a student from the filters above to view learning patterns
          </p>
        </div>
      </div>
    );
  }

  const isLoading = timePatternLoading || performanceLoading || velocityLoading || streakLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600">Loading pattern analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="h4">Learning Patterns & Insights</h2>
        <p className="text-gray-600 mt-1">
          Analyze study habits, performance trends, and learning velocity
        </p>
      </div>

      {/* Section 1: Streak Analysis */}
      <section>
        <h3 className="h5 mb-4">Study Streak & Consistency</h3>
        <StreakDisplay data={streakData?.streakAnalysis} />
      </section>

      {/* Section 2: Review Time Patterns */}
      <section>
        <h3 className="h5 mb-4">Review Time Patterns</h3>
        <div className="panel p-6">
          <ReviewTimeChart data={timePatternData?.reviewTimePatterns || []} />
        </div>
      </section>

      {/* Section 3: Performance Trends */}
      <section>
        <h3 className="h5 mb-4">Performance Over Time</h3>
        <div className="panel p-6">
          <PerformanceTrendChart data={performanceData?.performanceTrends || []} />
        </div>
      </section>

      {/* Section 4: Learning Velocity */}
      <section>
        <h3 className="h5 mb-4">Learning Velocity</h3>
        <div className="panel p-6">
          <LearningVelocityChart data={velocityData?.learningVelocity || []} />
        </div>
      </section>
    </div>
  );
}
